import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Navigation from './components/common/Navigation';
import Footer from './components/common/Footer';
import Dashboard from './pages/Dashboard';
import Languages from './pages/Languages';
import Learn from './pages/Learn';
import SystemDesign from './pages/SystemDesign';
import Practice from './pages/Practice';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import NodeJSPractice from './pages/learn/NodeJSPractice';
import DockerPractice from './pages/learn/DockerPractice';
import DatabasesPractice from './pages/learn/DatabasesPractice';
import JavaScriptPractice from './pages/languages/JavaScriptPractice';
import SQLPractice from './pages/languages/SQLPractice';
import Profile from './pages/Profile';
import Contact from './pages/Contact';
import { PrivacyPolicy, TermsOfService } from './pages/LegalPages';

// Practice Pages
import ParkingLotLLD from './pages/practice/ParkingLotLLD';
import DSAPractice from './pages/practice/DSAPractice';
import MachineCoding from './pages/practice/MachineCoding';
import MachineCodingWorkspace from './pages/practice/MachineCodingWorkspace';
import MachineCodingAccessGuard from './components/practice/MachineCodingAccessGuard';
import LLDPractice from './pages/practice/LLDPractice';
import HLDPractice from './pages/practice/HLDPractice';
import HLDPracticeHub from './pages/practice/HLDPracticeHub';
import HLDFundamentalsArticle from './pages/practice/HLDFundamentalsArticle';
import HLDProblemArticle from './pages/practice/HLDProblemArticle';
import HLDPracticeWorkspace from './pages/practice/HLDPracticeWorkspace';
import CustomHLDWorkspaceSetup from './pages/practice/CustomHLDWorkspaceSetup';
import HLDSharedSnapshot from './pages/practice/HLDSharedSnapshot';
import SystemDesignPractice from './pages/learn/SystemDesignPractice';
import DynamicContentPage from './components/content/DynamicContentPage';

// LLD Practice Pages (new practice workspace)
import ProblemCatalog from './pages/practice/lld/ProblemCatalog';
import AnalyticsDashboard from './pages/practice/lld/AnalyticsDashboard';
import ModeSelection from './pages/practice/lld/ModeSelection';
import ProblemWorkspace from './pages/practice/lld/ProblemWorkspace';
import CustomProblemForm from './pages/practice/lld/CustomProblemForm';
import EvaluationResults from './pages/practice/lld/EvaluationResults';
import LLDLegacyRedirect from './components/routing/LLDLegacyRedirect';

// System Design Topic Pages
import SystemDesignAllTopics from './pages/system-design/SystemDesignAllTopics';
// DISABLED: Custom rich topic pages — uncomment when these are production-ready
// import ScalabilityDetailV2 from './pages/system-design/ScalabilityDetailV2';
// import TopicPage from './pages/system-design/TopicPage';

// Lazy-loaded at the route level to keep Python (and Pyodide) out of the initial bundle
const PythonPractice = React.lazy(() => import('./pages/languages/PythonPractice'));
// Lazy-loaded to keep the TypeScript compiler (loaded on first run) out of the initial bundle
const TypeScriptPractice = React.lazy(() => import('./pages/languages/TypeScriptPractice'));

// System Design — HLD Workspace section. Every area view is lazy-loaded so no
// HLD view code ships in the initial application bundle (Req 1.5, 19.3). These
// loaders are module-scope constants so their identity is stable across renders,
// which the LazyArea retry memoization below relies on.
// HLD area views are lazy-loaded (kept out of the initial bundle, Req 1.5/19.3)
// but rendered INSIDE the main application layout so the persistent Navigation
// never remounts when moving between HLD pages (smooth in-app routing).
const HLDSectionHome = React.lazy(() => import('./pages/hld/HLDSectionHome'));
const HLDFundamentalsPage = React.lazy(() => import('./pages/hld/FundamentalsPage'));
const HLDBuildingBlocksPage = React.lazy(() => import('./pages/hld/BuildingBlocksPage'));
const HLDFrameworkPage = React.lazy(() => import('./pages/hld/FrameworkPage'));
const HLDEstimationGuidePage = React.lazy(() => import('./pages/hld/EstimationGuidePage'));
const HLDProblemCatalog = React.lazy(() => import('./pages/hld/ProblemCatalog'));
const HLDWorkspace = React.lazy(() => import('./pages/hld/Workspace'));

/** Shared Suspense wrapper for lazily-loaded HLD area views. */
function HLDSuspense({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-[60vh]" role="status" aria-live="polite">
          <div className="h-8 w-8 rounded-full border-2 border-sky-600 border-t-transparent animate-spin" />
          <span className="sr-only">Loading…</span>
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

/**
 * Resets scroll to the top on every route change (generic, app-wide).
 * Most pages scroll the window; some (e.g. PracticeContentLayout) use an inner
 * scroll container, so we also reset any tagged/overflow scroll containers.
 * Lesson/workspace content is often lazy-loaded, so we re-assert "top" over the
 * next few moments to beat the browser's scroll anchoring once tall content
 * mounts.
 */
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    const toTop = () => {
      window.scrollTo(0, 0);
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      // Inner scroll containers (e.g. the lesson content area, fullscreen panels).
      document
        .querySelectorAll<HTMLElement>('[data-content-area], [data-scroll-reset]')
        .forEach((el) => {
          el.scrollTop = 0;
        });
    };

    toTop();
    const raf = requestAnimationFrame(toTop);
    const timers = [60, 200, 500].map((ms) => setTimeout(toTop, ms));
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };
  }, [pathname]);

  return null;
}

/**
 * (HLD_Section routes now live inside the main application layout below, so the
 * persistent Navigation is shared and never remounts between HLD pages.)
 */

function AppRoutes() {
  const { state } = useAuth();
  const location = useLocation();

  return (
    <Routes>
      {/* Auth Routes (public, redirect if authenticated) */}
      <Route path="/login" element={
        state.isAuthenticated
          ? <Navigate to={(location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/dashboard'} replace />
          : <Login key={location.pathname} />
      } />
      <Route path="/signup" element={
        state.isAuthenticated ? <Navigate to="/dashboard" replace /> : <Signup key={location.pathname} />
      } />

      {/* Contact is public so users can request account or sign-in help. */}
      <Route path="/contact" element={
        <>
          <Navigation />
          <main className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
            <div className="flex-1"><Contact /></div>
            <Footer />
          </main>
        </>
      } />
      <Route path="/privacy" element={
        <>
          <Navigation />
          <main className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
            <div className="flex-1"><PrivacyPolicy /></div>
            <Footer />
          </main>
        </>
      } />
      <Route path="/terms" element={
        <>
          <Navigation />
          <main className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
            <div className="flex-1"><TermsOfService /></div>
            <Footer />
          </main>
        </>
      } />

      {/* Main Application Routes (with navigation, protected) */}
      <Route path="/*" element={
        <ProtectedRoute requireAuth={true}>
          <>
            <Navigation />
            <main className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
              <div className="flex-1 min-h-0">
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/languages" element={<Languages />} />
                  <Route path="/learn" element={<Learn />} />
                  <Route path="/topics" element={<Learn />} /> {/* Redirect old /topics to /learn */}
                  <Route path="/system-design" element={<SystemDesign />} />
                  <Route path="/practice" element={<Practice />} />
                  <Route path="/practice/hld" element={<HLDPracticeHub />} />
                  <Route path="/practice/hld/fundamentals" element={<Navigate to="/practice/hld/fundamentals/scale-from-zero-to-millions" replace />} />
                  <Route path="/practice/hld/fundamentals/:topicId" element={<HLDFundamentalsArticle />} />
                  <Route path="/practice/hld/problems" element={<Navigate to="/practice/hld/problems/rate-limiter" replace />} />
                  <Route path="/practice/hld/custom/new" element={<CustomHLDWorkspaceSetup />} />
                  <Route path="/practice/hld/shared/:token" element={<HLDSharedSnapshot />} />
                  <Route path="/practice/hld/custom/:workspaceId" element={<HLDPracticeWorkspace />} />
                  <Route path="/practice/hld/problems/:topicId/workspace" element={<HLDPracticeWorkspace />} />
                  <Route path="/practice/hld/problems/:topicId" element={<HLDProblemArticle />} />

                  {/* System Design — HLD Workspace section (shares this layout's
                      persistent Navigation for smooth in-app routing). Views are
                      lazy-loaded to stay out of the initial bundle. */}
                  <Route path="/hld" element={<HLDSuspense><HLDSectionHome /></HLDSuspense>} />
                  <Route path="/hld/fundamentals" element={<HLDSuspense><HLDFundamentalsPage /></HLDSuspense>} />
                  <Route path="/hld/fundamentals/:slug" element={<HLDSuspense><HLDFundamentalsPage /></HLDSuspense>} />
                  <Route path="/hld/building-blocks" element={<HLDSuspense><HLDBuildingBlocksPage /></HLDSuspense>} />
                  <Route path="/hld/building-blocks/:slug" element={<HLDSuspense><HLDBuildingBlocksPage /></HLDSuspense>} />
                  <Route path="/hld/framework" element={<HLDSuspense><HLDFrameworkPage /></HLDSuspense>} />
                  <Route path="/hld/estimation" element={<HLDSuspense><HLDEstimationGuidePage /></HLDSuspense>} />
                  <Route path="/hld/problems" element={<HLDSuspense><HLDProblemCatalog /></HLDSuspense>} />
                  {/* Review/Interview are workspace MODES, not a separate page. Old
                      /hld/review links now land on the single problem catalog. */}
                  <Route path="/hld/review" element={<Navigate to="/hld/problems" replace />} />
                  <Route path="/hld/workspace/:problemId" element={<HLDSuspense><HLDWorkspace /></HLDSuspense>} />

                  {/* Learn/Topic Detail Pages */}
                  <Route path="/learn/nodejs/getting-started" element={<NodeJSPractice />} />
                  <Route path="/learn/nodejs/:topicId?" element={<NodeJSPractice />} />
                  <Route path="/learn/docker/:topicId?" element={<DockerPractice />} />
                  <Route path="/learn/databases/:topicId?" element={<DatabasesPractice />} />

                  {/* System Design Routes - specific routes first */}
                  <Route path="/system-design/lld/parking-lot" element={<ParkingLotLLD />} />
                  <Route path="/system-design/lld" element={<LLDLegacyRedirect />} />
                  <Route path="/system-design/lld/:topicId" element={<LLDPractice />} />
                  <Route path="/system-design/hld/:topicId?" element={<HLDPractice />} />
                  {/* Design Patterns routes are handled by SystemDesignPractice dynamic route */}

                  {/* Dynamic Content Pages - These pages are driven by backend data */}
                  <Route path="/content/:slug" element={<DynamicContentPage />} />

                  {/* DISABLED: Custom rich topic pages — uncomment when these are production-ready
                  <Route path="/system-design/scalability" element={<ScalabilityDetailV2 />} />
                  <Route path="/system-design/caching" element={<TopicPage />} />
                  <Route path="/system-design/load-balancing" element={<TopicPage />} />
                  */}

                  {/* System Design Practice Page - handles all system design topics dynamically */}
                  <Route path="/system-design/:topicId?" element={<SystemDesignPractice />} />

                  {/* Legacy System Design Detail Pages - Keep for backward compatibility but these should redirect */}
                  <Route path="/system-design/all-topics" element={<SystemDesignAllTopics />} />

                  {/* Legacy routes - redirect to system-design */}
                  <Route path="/learn/lld/parking-lot" element={<ParkingLotLLD />} />
                  <Route path="/learn/lld/:topicId?" element={<LLDPractice />} />
                  <Route path="/learn/system-design/:topicId?" element={<SystemDesignPractice />} />
                  <Route path="/learn/design-patterns" element={<Navigate to="/system-design" replace />} />

                  {/* Legacy Topic Routes (redirect to /learn) */}
                  <Route path="/topics/nodejs" element={<Navigate to="/learn/nodejs" replace />} />
                  <Route path="/topics/nodejs/getting-started" element={<Navigate to="/learn/nodejs" replace />} />
                  <Route path="/topics/docker" element={<Navigate to="/learn/docker" replace />} />
                  <Route path="/topics/system-design" element={<Navigate to="/system-design" replace />} />
                  <Route path="/topics/databases" element={<Navigate to="/learn/databases" replace />} />
                  <Route path="/topics/:id" element={<Navigate to="/learn" replace />} />

                  {/* System Design Topic Pages */}
                  <Route path="/system-design/all-topics" element={<SystemDesignAllTopics />} />

                  {/* Language Detail Pages */}
                  <Route path="/languages/javascript/:topicId?" element={<JavaScriptPractice />} />
                  <Route path="/languages/sql/:topicId?" element={<SQLPractice />} />
                  <Route path="/languages/typescript/:topicId?" element={
                    <Suspense fallback={
                      <div className="flex items-center justify-center min-h-[60vh]">
                        <div className="h-8 w-8 rounded-full border-2 border-sky-600 border-t-transparent animate-spin" />
                      </div>
                    }>
                      <TypeScriptPractice />
                    </Suspense>
                  } />
                  <Route path="/languages/python/:topicId?" element={
                    <Suspense fallback={
                      <div className="flex items-center justify-center min-h-[60vh]">
                        <div className="h-8 w-8 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" />
                      </div>
                    }>
                      <PythonPractice />
                    </Suspense>
                  } />
                  <Route path="/languages/:id" element={<Navigate to="/languages" replace />} />

                  {/* Practice Detail Pages */}
                  <Route path="/practice/dsa/:section?" element={<DSAPractice />} />
                  <Route path="/practice/machine-coding" element={<MachineCodingAccessGuard><MachineCoding /></MachineCodingAccessGuard>} />
                  <Route path="/practice/machine-coding/workspace/:templateId" element={<MachineCodingAccessGuard><MachineCodingWorkspace /></MachineCodingAccessGuard>} />
                  <Route path="/practice/machine-coding/workspace/:templateId/:slug" element={<MachineCodingAccessGuard><MachineCodingWorkspace /></MachineCodingAccessGuard>} />

                  {/* LLD Practice routes (new interactive workspace) */}
                  <Route path="/practice/lld" element={<ProblemCatalog />} />
                  <Route path="/practice/lld/analytics" element={<AnalyticsDashboard />} />
                  <Route path="/practice/lld/parking-lot" element={<ParkingLotLLD />} />
                  {/* Custom problem authoring — must be registered BEFORE the dynamic :problemId routes */}
                  <Route path="/practice/lld/custom/new" element={<CustomProblemForm mode="create" />} />
                  <Route path="/practice/lld/custom/:id/edit" element={<CustomProblemForm mode="edit" />} />
                  <Route path="/practice/lld/:problemId/mode" element={<ModeSelection />} />
                  <Route path="/practice/lld/:problemId/evaluation" element={<EvaluationResults />} />
                  <Route path="/practice/lld/:problemId/:mode" element={<ProblemWorkspace />} />

                  <Route path="/profile" element={<Profile />} />

                  {/* Catch all route */}
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </div>
              <Footer />
            </main>
          </>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  // Handle Google OAuth callback — token arrives in the URL hash after redirect
  const [oauthProcessing, setOauthProcessing] = React.useState(() => {
    return window.location.hash.includes('access_token=');
  });

  React.useEffect(() => {
    if (!oauthProcessing) return;

    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    const accessToken = params.get('access_token');

    // Clean the URL hash immediately
    window.history.replaceState(null, '', '/dashboard');

    if (!accessToken) {
      setOauthProcessing(false);
      return;
    }

    const processLogin = async () => {
      try {
        const { authService } = await import('./services/authService');
        await authService.loginWithGoogle(accessToken);
        // Tokens stored — hard reload to /dashboard so AuthContext initializes fresh
        window.location.reload();
      } catch (error) {
        console.error('Google login failed:', error);
        window.location.replace('/login');
      }
    };
    processLogin();
  }, [oauthProcessing]);

  if (oauthProcessing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f9fafb' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 32, height: 32, border: '3px solid #e5e7eb', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Signing you in...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <div className="min-h-screen bg-gray-100 dark:bg-gray-900 transition-colors duration-300">
            <AppRoutes />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
